// src/components/pages/.../ProductConsultForm.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Backdrop,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { alpha } from "@mui/material/styles";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";
import { getLangFromCookie } from "../../../../Utils/LangCookie";
import { ProductConsultingAPI } from "../../../../api/productConsulting";

// ✅ Auth (to detect login + read user profile)
import { useAuth } from "../../Auth/AuthContext"; // adjust path if your AuthContext is elsewhere

// ✅ Success dialog (reusable)
import SuccessSubmitDialog from "../../../GlobalFunction/SuccessSubmit";

/* ------------------ i18n dictionary ------------------ */
const I18N = {
  ZH: {
    title: "專業諮詢",
    labels: {
      company: "公司名稱",
      name: "姓名/職稱",
      phone: "電話號碼",
      email: "電子郵件",
      subject: "諮詢主題",
      detail: "諮詢詳情",
    },
    requiredFlag: "【必填】",
    requiredField: "此欄為必填",
    agreeLabel: "我同意以上內容",
    agreeError: "請勾選同意以上內容",
    notices: [
      "我們將根據《個人資訊保護法》等法規妥善處理您的個人資訊與諮詢內容。",
      "請確認您的電子郵件地址正確，以便我們回覆您的諮詢。",
      "工作日（國定假日除外）09:00–18:00回覆。",
    ],
    submit: "送出",
    submitted: "已送出您的諮詢，我們將盡快與您聯繫！",
    submitFailed: "送出失敗，請稍後再試或聯絡客服。",
    submitting: "送出中…",
  },

  EN: {
    title: "Professional Consultation",
    labels: {
      company: "Company Name",
      name: "name/job title",
      phone: "Phone Number",
      email: "E-mail Address",
      subject: "Subject",
      detail: "Inquiry Details",
    },
    requiredFlag: "[Required]",
    requiredField: "This field is required.",
    agreeLabel: "I agree to the above terms.",
    agreeError: "Please check the box to agree to the terms.",
    notices: [
      "We will handle your personal information and inquiry in accordance with the Personal Data Protection Act and related regulations.",
      "Please confirm that your e-mail address is correct so we can reply to you.",
      "Replies will be provided on business days (excluding national holidays) between 09:00–18:00.",
    ],
    submit: "Submit",
    submitted: "Your inquiry has been sent. We will get back to you shortly.",
    submitFailed: "Submission failed. Please try again later.",
    submitting: "Submitting...",
  },

  JP: {
    title: "専門相談のお申し込み",
    labels: {
      company: "会社名",
      name: "氏名 / 役職",
      phone: "電話番号",
      email: "メールアドレス",
      subject: "ご相談内容（件名）",
      detail: "ご相談の詳細",
    },
    requiredFlag: "【必須】",
    requiredField: "この項目は必須です。",
    agreeLabel: "上記の内容に同意します。",
    agreeError: "次へ進むには、規約にご同意いただく必要があります。",
    notices: [
      "お客様からいただいた個人情報およびお問い合わせ内容は、個人情報保護法等の法令に基づき、適切に管理いたします。",
      "うまく返信が届くよう、メールアドレスに誤りがないようにご確認ください。",
      "営業時間：9:00〜18:00（土日祝日を除く）",
    ],
    submit: "送信する",
    submitted: "お問い合わせには、翌営業日以降に順次返信いたします。",
    submitFailed:
      "送信に失敗しました。しばらくしてからもう一度お試しください。",
    submitting: "送信中…",
  },
};

/* ---------- helpers for the table-like layout ---------- */
const LabelCell = ({ children, required, requiredLabel }) => (
  <Box
    sx={(t) => ({
      bgcolor: t.palette.mode === "light" ? "grey.50" : alpha("#fff", 0.04),
      borderRight: { md: `1px solid ${t.palette.divider}` },
      p: 1.25,
      display: "flex",
      alignItems: "center",
      minHeight: 56,
    })}
  >
    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
      {children}{" "}
      {required && (
        <Typography
          component="span"
          color="error.main"
          sx={{ fontWeight: 700 }}
        >
          {requiredLabel}
        </Typography>
      )}
    </Typography>
  </Box>
);

const Row = ({ label, required, requiredLabel, children }) => (
  <Box
    sx={(t) => ({
      display: "grid",
      gridTemplateColumns: { xs: "1fr", md: "240px 1fr" },
      borderBottom: `1px solid ${t.palette.divider}`,
      "&:first-of-type": { borderTop: `1px solid ${t.palette.divider}` },
    })}
  >
    <LabelCell required={required} requiredLabel={requiredLabel}>
      {label}
    </LabelCell>
    <Box sx={{ p: 1.25 }}>{children}</Box>
  </Box>
);

function normalizeLang(lang) {
  const s = String(lang || "")
    .toUpperCase()
    .trim();
  return s === "EN" || s === "JP" || s === "ZH" ? s : "ZH";
}

function isBlank(v) {
  return !String(v ?? "").trim();
}

function uniqNonBlank(list) {
  const out = [];
  const seen = new Set();
  for (const v of list || []) {
    const s = String(v ?? "").trim();
    if (!s) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

/**
 * Extract profile fields from your WP viewer shape.
 * Your provided data:
 *   viewer.name, viewer.email, viewer.phoneNumber
 *
 * But in-app your AuthContext might expose these directly on user,
 * so we support both shapes.
 */
function pickProfile(user) {
  const name = String(user?.name || user?.viewer?.name || "").trim();
  const email = String(user?.email || user?.viewer?.email || "").trim();
  const phone = String(
    user?.phoneNumber || user?.viewer?.phoneNumber || "",
  ).trim();
  return { name, email, phone };
}

export default function ProductConsultForm({ mode = "b2b" }) {
  const accent = getPrimaryColor(mode);

  // ✅ Always jump to top on page load / refresh (NO smooth)
  useEffect(() => {
    // cancel any pending scroll restoration from the browser/router
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    // jump immediately
    window.scrollTo(0, 0);

    // also re-assert on next frame (covers autofocus/layout shifts)
    requestAnimationFrame(() => window.scrollTo(0, 0));
  }, []);

  /* ---------------- language sync ---------------- */
  const [lang, setLang] = useState(() =>
    normalizeLang(getLangFromCookie() || "ZH"),
  );

  useEffect(() => {
    const handle = () => setLang(normalizeLang(getLangFromCookie() || "ZH"));
    window.addEventListener("app:languageChanged", handle);
    return () => window.removeEventListener("app:languageChanged", handle);
  }, []);

  const dict = I18N[lang] || I18N.ZH;

  /* ---------------- auth (for prefill) ---------------- */
  const { user, loading: authLoading } = useAuth();
  const isLoggedIn = !!user && !authLoading;

  /* ---------------- suggestions (only if logged in) ---------------- */
  const [suggest, setSuggest] = useState({
    name: [],
    email: [],
    phone: [],
  });

  /* ---------------- form state ---------------- */
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    subject: "",
    detail: "",
    agree: false,
  });

  const [errors, setErrors] = useState({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [isSending, setIsSending] = useState(false);
  const [serverError, setServerError] = useState("");

  // ✅ success dialog open state
  const [successOpen, setSuccessOpen] = useState(false);

  // ✅ required: all except subject
  const requiredKeys = useMemo(
    () => ["company", "name", "phone", "email", "detail"],
    [],
  );

  const trim = (v) => String(v ?? "").trim();

  const validate = (nextForm) => {
    const next = {};
    for (const k of requiredKeys) {
      if (!trim(nextForm[k])) next[k] = true;
    }
    if (!nextForm.agree) next.agree = true;
    return next;
  };

  // ✅ Prefill + suggestions only when logged in; otherwise keep default "" as usual.
  useEffect(() => {
    if (!isLoggedIn) {
      setSuggest({ name: [], email: [], phone: [] });
      return;
    }

    const { name, email, phone } = pickProfile(user);

    setSuggest({
      name: uniqNonBlank([name]),
      email: uniqNonBlank([email]),
      phone: uniqNonBlank([phone]),
    });

    // prefill ONLY if blank (still editable)
    setForm((prev) => {
      const next = { ...prev };
      if (isBlank(next.name) && name) next.name = name;
      if (isBlank(next.email) && email) next.email = email;
      if (isBlank(next.phone) && phone) next.phone = phone;
      return next;
    });
  }, [isLoggedIn, user]);

  // language change: keep error flags up to date
  useEffect(() => {
    if (!submitAttempted) return;
    setErrors(validate(form));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  const update = (k) => (e) => {
    const value = e.target.value;

    setForm((prev) => {
      const nextForm = { ...prev, [k]: value };

      if (submitAttempted) {
        setErrors(validate(nextForm));
      } else {
        setErrors((prevErr) => {
          if (!prevErr?.[k]) return prevErr;
          if (!trim(value)) return prevErr;
          const cp = { ...prevErr };
          delete cp[k];
          return cp;
        });
      }

      return nextForm;
    });
  };

  const onToggleAgree = (e) => {
    const checked = !!e.target.checked;

    setForm((prev) => {
      const nextForm = { ...prev, agree: checked };

      if (submitAttempted) {
        setErrors(validate(nextForm));
      } else {
        setErrors((prevErr) => {
          if (!prevErr?.agree) return prevErr;
          if (!checked) return prevErr;
          const cp = { ...prevErr };
          delete cp.agree;
          return cp;
        });
      }

      return nextForm;
    });
  };

  const handleSubmit = async (e) => {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    if (isSending) return; // ✅ double-submit guard

    setServerError("");
    setSubmitAttempted(true);

    const next = validate(form);
    setErrors(next);
    if (Object.keys(next).length) return;

    setIsSending(true);
    try {
      await ProductConsultingAPI.create(form, { lang, mode });

      // ✅ reset
      setForm({
        name: "",
        email: "",
        company: "",
        phone: "",
        subject: "",
        detail: "",
        agree: false,
      });
      setErrors({});
      setSubmitAttempted(false);

      // ✅ open success dialog
      setSuccessOpen(true);
    } catch (err) {
      if (err?.field) setErrors((prev) => ({ ...prev, [err.field]: true }));
      setServerError(err?.message || dict.submitFailed);
    } finally {
      setIsSending(false);
    }
  };

  const tfCommon = {
    size: "small",
    fullWidth: true,
    InputLabelProps: { shrink: true },
    disabled: isSending,
  };

  return (
    <Box sx={{ bgcolor: "background.default", p: { xs: 2, md: 3 } }}>
      {/* ✅ Loading screen while API is running */}
      <Backdrop
        open={isSending}
        sx={{
          zIndex: (t) => t.zIndex.modal + 10,
          color: "#fff",
        }}
      >
        <Box
          sx={{
            bgcolor: "rgba(17,24,39,0.85)",
            px: 3,
            py: 2.5,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <CircularProgress size={22} />
          <Typography sx={{ fontWeight: 800 }}>{dict.submitting}</Typography>
        </Box>
      </Backdrop>

      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        {dict.title}
      </Typography>

      {/* ✅ Success dialog */}
      <SuccessSubmitDialog
        open={successOpen}
        onClose={() => setSuccessOpen(false)}
        primaryHref="/b2b/商品資訊"
        primaryLabel={{
          ZH: "返回商品資訊頁面",
          EN: "Browse Products",
          JP: "商品情報ページに戻る",
        }}
        secondaryHref="/"
        secondaryLabel={{
          ZH: "回到首頁",
          EN: "Back to Home",
          JP: "ホームへ戻る",
        }}
      />

      {/* ✅ IMPORTANT: button must be INSIDE this form */}
      <Paper
        component="form"
        onSubmit={handleSubmit}
        noValidate
        variant="outlined"
        sx={{ borderRadius: 1.5, overflow: "hidden", my: 4 }}
      >
        <Row
          label={dict.labels.company}
          required
          requiredLabel={dict.requiredFlag}
        >
          <TextField
            {...tfCommon}
            value={form.company}
            onChange={update("company")}
            error={!!errors.company}
            helperText={errors.company ? dict.requiredField : " "}
          />
        </Row>

        {/* ✅ Name: suggestion from user DB only when logged in */}
        <Row
          label={dict.labels.name}
          required
          requiredLabel={dict.requiredFlag}
        >
          <Autocomplete
            freeSolo
            options={isLoggedIn ? suggest.name : []}
            value={form.name || ""}
            onInputChange={(e, v) => setForm((s) => ({ ...s, name: v }))}
            onChange={(e, v) => setForm((s) => ({ ...s, name: v || "" }))}
            disabled={isSending}
            renderInput={(params) => (
              <TextField
                {...params}
                {...tfCommon}
                error={!!errors.name}
                helperText={errors.name ? dict.requiredField : " "}
              />
            )}
          />
        </Row>

        {/* ✅ Phone: suggestion from user DB only when logged in */}
        <Row
          label={dict.labels.phone}
          required
          requiredLabel={dict.requiredFlag}
        >
          <Autocomplete
            freeSolo
            options={isLoggedIn ? suggest.phone : []}
            value={form.phone || ""}
            onInputChange={(e, v) => setForm((s) => ({ ...s, phone: v }))}
            onChange={(e, v) => setForm((s) => ({ ...s, phone: v || "" }))}
            disabled={isSending}
            renderInput={(params) => (
              <TextField
                {...params}
                {...tfCommon}
                error={!!errors.phone}
                helperText={errors.phone ? dict.requiredField : " "}
              />
            )}
          />
        </Row>

        {/* ✅ Email: suggestion from user DB only when logged in */}
        <Row
          label={dict.labels.email}
          required
          requiredLabel={dict.requiredFlag}
        >
          <Autocomplete
            freeSolo
            options={isLoggedIn ? suggest.email : []}
            value={form.email || ""}
            onInputChange={(e, v) => setForm((s) => ({ ...s, email: v }))}
            onChange={(e, v) => setForm((s) => ({ ...s, email: v || "" }))}
            disabled={isSending}
            renderInput={(params) => (
              <TextField
                {...params}
                {...tfCommon}
                type="email"
                error={!!errors.email}
                helperText={errors.email ? dict.requiredField : " "}
              />
            )}
          />
        </Row>

        <Row label={dict.labels.subject}>
          <TextField
            {...tfCommon}
            value={form.subject}
            onChange={update("subject")}
          />
        </Row>

        <Row
          label={dict.labels.detail}
          required
          requiredLabel={dict.requiredFlag}
        >
          <TextField
            {...tfCommon}
            multiline
            minRows={6}
            value={form.detail}
            onChange={update("detail")}
            error={!!errors.detail}
            helperText={errors.detail ? dict.requiredField : " "}
          />
        </Row>

        <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 2 }}>
          <Button
            type="submit"
            variant="contained"
            disabled={isSending}
            sx={{
              px: 6,
              py: 1,
              fontWeight: 700,
              bgcolor: accent,
              "&:hover": { bgcolor: alpha(accent, 0.85) },
            }}
          >
            {isSending ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CircularProgress size={18} color="inherit" />
                <span>{dict.submit}</span>
              </Box>
            ) : (
              dict.submit
            )}
          </Button>
        </Box>
      </Paper>

      {/* Notice */}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: 1.5,
          borderColor: (t) => alpha(accent, 0.28),
        }}
      >
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.8 }}>
          {dict.notices.map((n, i) => (
            <li key={i}>{n}</li>
          ))}
        </ul>
      </Paper>

      {/* Consent */}
      <Box sx={{ textAlign: "center", my: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={form.agree}
              onChange={onToggleAgree}
              disabled={isSending}
            />
          }
          label={
            <Typography sx={{ fontWeight: 600 }}>{dict.agreeLabel}</Typography>
          }
        />

        {errors.agree && (
          <Typography variant="caption" color="error" display="block">
            {dict.agreeError}
          </Typography>
        )}
      </Box>

      {!!serverError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {serverError}
        </Alert>
      )}
    </Box>
  );
}
